import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BasicCli from '../../main/components/basicCli/BasicCli';
import { ApiConstants } from '../../main/constants/ApiConstants';
import axiosInstance from '../../main/initializers/axios';
import DeleteModal from '../../main/components/modal/delete/DeleteModal';
import EditModal from '../../main/components/modal/edit/EditModal';
import eFormType from '../../main/assets/enums/eFormType';
import IUser from '../../main/interfaces/IUser';
import IRole from '../../main/interfaces/IRole';
import IPagination from '../../main/interfaces/IPagination';
import IGridItems from '../../main/interfaces/IGridItems';
import GeneralTable from '../../main/components/grid/GeneralTable';
import './NotificationAdmin.css';

type Category = { id: number; name: string; type: number };
type UserCategoryRow = {
  id: number;
  companyId: number;
  categoryId: number;
  category?: string;
  targetType: string;
  type?: string;
  roleId?: number | null;
  role?: string;
  userId?: number | null;
  user?: string;
  isActive: boolean;
  createdAt?: string;
};

const emptyRow: UserCategoryRow = {
  id: 0,
  companyId: 0,
  categoryId: 0,
  targetType: 'AllCompanyUsers',
  roleId: null,
  userId: null,
  isActive: true,
};

const normalizeUserCategoryForm = (row: UserCategoryRow): UserCategoryRow => {
  const targetType = row.targetType || 'AllCompanyUsers';

  return {
    ...row,
    targetType,
    roleId: targetType === 'Role' ? row.roleId ?? null : null,
    userId: targetType === 'User' ? row.userId ?? null : null,
  };
};

const defaultUserCategoryGridItems: IGridItems[] = [
  { entity: 'Id', sort: 'desc', filter: null, isShown: true, label: 'Id' },
  { entity: 'Category', sort: null, filter: null, isShown: true, label: 'CategoryId' },
  { entity: 'Type', sort: null, filter: null, isShown: true, label: 'TargetType' },
  { entity: 'Role', sort: null, filter: null, isShown: true, label: 'RoleId' },
  { entity: 'User', sort: null, filter: null, isShown: true, label: 'UserId' },
  { entity: 'IsActive', sort: null, filter: null, isShown: true, label: 'IsActive' },
  { entity: 'CreatedAt', sort: null, filter: null, isShown: true, label: 'CreatedAt' },
];

const normalizeUserCategoryGridItems = (items?: IGridItems[]) => {
  const validEntities = new Set(['id', 'categoryid', 'category', 'targettype', 'type', 'roleid', 'role', 'userid', 'user', 'isactive', 'createdat']);
  const nextItems = (items ?? []).filter((x) => validEntities.has(String(x.entity ?? '').toLowerCase()));
  return nextItems.length ? nextItems : defaultUserCategoryGridItems;
};

const NotificationUserCategoriesGrid = () => {
  const { i18n, t } = useTranslation();
  const [rows, setRows] = useState<UserCategoryRow[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [roles, setRoles] = useState<IRole[]>([]);
  const [users, setUsers] = useState<IUser[]>([]);
  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [pagination, setPagination] = useState<IPagination>({ count: 1, page: 1, itemPerPage: 50, itemsPerPage: [10, 20, 50, 100] });
  const [selected, setSelected] = useState<UserCategoryRow | null>(null);
  const [form, setForm] = useState<UserCategoryRow>(emptyRow);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const targetTypeLabel = useCallback(
    (targetType?: string) =>
      t(`notifications.targetTypes.${targetType}`, {
        defaultValue: targetType || '',
      }),
    [t],
  );

  const translatedRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        type: targetTypeLabel(row.targetType || row.type),
      })),
    [rows, targetTypeLabel],
  );

  const load = useCallback(
    async (pag?: IPagination, gridOverride?: IGridItems[]) => {
      const effective = pag ?? pagination;
      const effectiveGrid = gridOverride ?? gridItems ?? [];
      const [rulesRes, categoriesRes, rolesRes, usersRes] = await Promise.all([
        axiosInstance.post(ApiConstants.notifications.userCategories.listCt, {
          gridItems: effectiveGrid,
          pagination: effective,
        }),
        axiosInstance.get(ApiConstants.notifications.categories.list),
        axiosInstance.get(ApiConstants.Roles.list),
        axiosInstance.get(ApiConstants.User.users),
      ]);

      setRows(rulesRes.data?.items ?? []);
      setGridItems(normalizeUserCategoryGridItems(rulesRes.data?.gridItems ?? []));
      setPagination((prev) => ({
        ...prev,
        count: rulesRes.data?.pagination?.count ?? prev.count,
        page: rulesRes.data?.pagination?.page ?? prev.page,
        itemPerPage: rulesRes.data?.pagination?.itemPerPage ?? prev.itemPerPage,
        itemsPerPage: rulesRes.data?.pagination?.itemsPerPage ?? prev.itemsPerPage,
      }));
      setCategories(categoriesRes.data ?? []);
      setRoles(rolesRes.data ?? []);
      setUsers(usersRes.data ?? []);
    },
    [gridItems, pagination],
  );

  useEffect(() => {
    void load();
  }, []);

  const openNew = () => {
    setForm(normalizeUserCategoryForm(emptyRow));
    setSelected(null);
    setIsEditOpen(true);
  };

  const openEdit = (row: UserCategoryRow) => {
    setSelected(row);
    setForm(normalizeUserCategoryForm(row));
    setIsEditOpen(true);
  };

  const save = async () => {
    await axiosInstance.post(ApiConstants.notifications.userCategories.save, normalizeUserCategoryForm(form));
    setIsEditOpen(false);
    await load();
  };

  const remove = async () => {
    if (!selected) return;
    await axiosInstance.post(ApiConstants.notifications.userCategories.delete(selected.id));
    setIsDeleteOpen(false);
    setSelected(null);
    await load();
  };

  return (
    <BasicCli>
      <div className="naWrap bcPageContent">
        <div className="naContainer">
          <div className="bcPageHeader">
            <div>
              <h1 className="bcPageTitle">{i18n.t('notifications.userCategoryTitle', { defaultValue: 'Notification User Groups' })}</h1>
              <p className="naSubtitle">{i18n.t('notifications.userCategorySubtitle', { defaultValue: 'Configure who receives each notification category.' })}</p>
            </div>
            <div className="bcPageHeaderActions">
              <button className="ui-control ui-btn" type="button" onClick={() => void load()}>{i18n.t('notifications.refresh', { defaultValue: 'Refresh' })}</button>
              <button className="ui-control ui-btn ui-btn--primary" type="button" onClick={openNew}>{i18n.t('generic.createNew', { defaultValue: 'Create new' })}</button>
            </div>
          </div>

          <div className="naTableWrap">
            <GeneralTable
              data={translatedRows}
              columns={normalizeUserCategoryGridItems(gridItems)}
              gridEntity={20}
              title=""
              hasRp={true}
              selectedItem={(val) => setSelected(val)}
              onEdit={openEdit}
              onDelete={(row) => {
                setSelected(row);
                setIsDeleteOpen(true);
              }}
              pagination={pagination}
              selectedItemPagination={(val) => void load(val)}
              onFilterClose={(nextGridItems) => {
                const normalized = normalizeUserCategoryGridItems(nextGridItems);
                setGridItems(normalized);
                void load(undefined, normalized);
              }}
            />
          </div>
        </div>
      </div>

      {isEditOpen && (
        <EditModal
          title={form.id > 0
            ? i18n.t('notifications.editUserCategory', { defaultValue: 'Edit User Group Rule' })
            : i18n.t('notifications.newUserCategory', { defaultValue: 'New User Group Rule' })}
          width="760px"
          closeModal={() => setIsEditOpen(false)}
          setIsModalOpen={setIsEditOpen}
          onSubmitData={() => void save()}
          modalType={form.id > 0 ? eFormType.Edit : eFormType.New}
        >
          <div className="naForm">
            <label className="naField">
              <span>{i18n.t('notifications.category', { defaultValue: 'Category' })}</span>
              <select className="ui-control ui-select" value={form.categoryId} onChange={(e) => setForm((prev) => ({ ...prev, categoryId: Number(e.target.value) }))}>
                <option value={0}>{i18n.t('notifications.selectCategory', { defaultValue: 'Select category' })}</option>
                {categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            <label className="naField">
              <span>{i18n.t('notifications.targetType', { defaultValue: 'Target Type' })}</span>
              <select className="ui-control ui-select" value={form.targetType} onChange={(e) => setForm((prev) => normalizeUserCategoryForm({ ...prev, targetType: e.target.value }))}>
                <option value="AllCompanyUsers">{targetTypeLabel('AllCompanyUsers')}</option>
                <option value="OnlyAdmins">{targetTypeLabel('OnlyAdmins')}</option>
                <option value="Role">{targetTypeLabel('Role')}</option>
                <option value="User">{targetTypeLabel('User')}</option>
              </select>
            </label>
            <label className="naField">
              <span>{i18n.t('generic.role', { defaultValue: 'Role' })}</span>
              <select
                className="ui-control ui-select"
                value={form.targetType === 'Role' ? form.roleId ?? '' : ''}
                disabled={form.targetType !== 'Role'}
                onChange={(e) => setForm((prev) => ({ ...prev, roleId: e.target.value ? Number(e.target.value) : null }))}
              >
                <option value="">{i18n.t('generic.none', { defaultValue: 'None' })}</option>
                {roles.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            <label className="naField">
              <span>{i18n.t('menu.Users', { defaultValue: 'Users' })}</span>
              <select
                className="ui-control ui-select"
                value={form.targetType === 'User' ? form.userId ?? '' : ''}
                disabled={form.targetType !== 'User'}
                onChange={(e) => setForm((prev) => ({ ...prev, userId: e.target.value ? Number(e.target.value) : null }))}
              >
                <option value="">{i18n.t('generic.none', { defaultValue: 'None' })}</option>
                {users.map((item) => <option key={item.id} value={item.id}>{item.firstName} {item.lastName}</option>)}
              </select>
            </label>
            <label className="naCheck"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))} /> <span>{i18n.t('notifications.active', { defaultValue: 'Active' })}</span></label>
          </div>
        </EditModal>
      )}

      {isDeleteOpen && selected && (
        <DeleteModal width="460px" handleClose={() => setIsDeleteOpen(false)} handleDelete={() => void remove()}>
          {i18n.t('notifications.deleteUserCategoryConfirm', {
            defaultValue: 'Delete notification user group rule {{id}}?',
            id: selected.id,
          })}
        </DeleteModal>
      )}
    </BasicCli>
  );
};

export default NotificationUserCategoriesGrid;
